/**
 * Unified Document Configurations
 * 
 * Consolidated configurations for the full finance document suite:
 * - Sales: invoice, quote, credit_note, debit_note, proforma, sales_order, delivery_note, waybill, rma
 * - Purchases: purchase_order, supplier_invoice, expense (unified, incl. fuel/toll/etc), grn, supplier_credit, supplier_quote, purchase_requisition, rfq, purchase_return
 * - Operations: work_order
 * - Warehouse: picking_slip, packing_slip, stock_adjustment, transfer_order
 * 
 * Each config controls:
 * - Field labels
 * - Field visibility
 * - Status options
 * - Document conversions
 */

import { FileText, FileCheck, FileMinus, Package, Truck, ShoppingCart, Receipt, Inbox, RotateCcw, ClipboardList, FileQuestion, ClipboardCheck, CreditCard, Printer, Send, RefreshCw, Layers } from 'lucide-react';
import type { DocumentConfig } from './DocumentModal';
import { getWorkItemTypeRegistry } from '@/config/work-items/configLoader';
import type { StatusDefinition } from '@/config/work-items/types';
import { FINAL_ACTION_GATES, FINAL_ACTION_ITEM_CONDITIONS } from '@/config/document-modal/finalActionGates';
import { getDocumentModalRegistry } from '@/config/document-modal/configLoader';
import { resolveDocumentConfig } from '@/config/document-modal/documentConfigResolver';
import type { PromotionTargetYaml } from '@/config/document-modal/types';
import type { DocumentType, Document } from '@/hooks/useDocumentsWorkItems';

// ============================================================================
// SALES DOCUMENTS
// ============================================================================

/**
 * Final action menu for AR statement documents (invoice / credit_note /
 * debit_note), driven by the organization workflow mode carried on the doc
 * as `_workflowMode` (injected by DocumentModal from useFinanceSettings):
 *
 *   simplified → direct send/issue (owner-operated, no approval friction)
 *   standard   → submit for approval OR add to batch OR direct send —
 *                both release paths are valid
 *   strict     → batch is the only release path; no direct send/submit
 *                (segregation of duties — the batch approval posts the doc)
 */
type ArWorkflowDoc = Partial<Document> & { _workflowMode?: string };
const arDocMode = (doc: Partial<Document>) => (doc as ArWorkflowDoc)._workflowMode || 'standard';
const arDocIsDraft = (doc: Partial<Document>) => String(doc.status || 'draft') === 'draft';
const arDocIsIssued = (doc: Partial<Document>) =>
  ['sent', 'overdue', 'partial', 'partially_paid', 'viewed'].includes(String(doc.status || ''));

function arDocumentFinalActionMenu(opts: { batchable: boolean; sendLabel: string; resendLabel: string }): DocumentConfig['finalActionMenu'] {
  return {
    items: [
      { type: 'preview', label: 'Print Preview', icon: Printer, tone: 'primary' },
      ...(opts.batchable
        ? [{
            type: 'add_to_batch' as const,
            label: 'Add to Invoice Batch',
            icon: Layers,
            tone: 'primary' as const,
            condition: (doc: Partial<Document>) => arDocIsDraft(doc) && Boolean(doc.id) && ['standard', 'strict'].includes(arDocMode(doc)),
          }]
        : []),
      {
        type: 'submit_for_approval',
        label: 'Submit for Approval',
        icon: FileCheck,
        tone: 'primary',
        condition: (doc) => arDocIsDraft(doc) && arDocMode(doc) === 'standard',
      },
      {
        type: 'send',
        label: opts.sendLabel,
        tone: 'primary',
        // Direct send is only for simplified mode. Standard must go through
        // approval or a posted batch; strict must go through a posted batch.
        condition: (doc) => arDocIsDraft(doc) && arDocMode(doc) === 'simplified',
      },
      {
        type: 'send',
        label: opts.resendLabel,
        tone: 'primary',
        condition: (doc) => arDocIsIssued(doc),
      },
    ],
  };
}

export const INVOICE_CONFIG: DocumentConfig = {
  doc_type: 'invoice',

  labels: {
    title: 'Invoice',
    titleNew: 'Create New Invoice',
    titleEdit: 'Edit Invoice',
    numberField: 'Invoice Number',
    numberPlaceholder: 'e.g., INV-001',
    dateField: 'Invoice Date',
    contactLabel: 'Customer',
    saveButton: 'Save Invoice'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showDueDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'pending_approval', label: 'Pending Approval' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'sent', label: 'Sent' },
      { value: 'viewed', label: 'Viewed' },
      { value: 'overdue', label: 'Overdue' },
      { value: 'paid', label: 'Paid' },
      { value: 'partially_paid', label: 'Partially Paid' },
      { value: 'cancelled', label: 'Cancelled' }
    ],
    // GAAP Compliance: Only draft and pending_approval can be edited
    // Once sent to customer, use credit notes for corrections
    userEditableStatuses: ['draft', 'pending_approval', 'rejected'],
    showInvoiceType: true,
    invoiceTypeOptions: [
      { value: 'standard', label: 'Standard' },
      { value: 'recurring', label: 'Recurring' },
      { value: 'adjustment', label: 'Adjustment' }
    ],
    showPaymentTerms: true,
    showAmountPaid: true,
    showBalanceDue: true,
    showReference: true,
    showCustomerPO: true,
    showNotes: true,
    showTermsConditions: true,
    showMarkup: true
  },

  conversions: [
    {
      targetType: 'credit_note',
      label: 'Create Credit Note',
      icon: FileMinus,
      condition: (doc) => ['sent', 'paid', 'partially_paid'].includes(doc.status!)
    },
    {
      targetType: 'delivery_note',
      label: 'Create Delivery Note',
      icon: Truck,
      condition: (doc) => doc.status !== 'cancelled'
    }
  ],

  finalActionMenu: arDocumentFinalActionMenu({
    batchable: true,
    sendLabel: 'Complete & Send',
    resendLabel: 'Resend Invoice',
  }),
};

export const PROPOSAL_CONFIG: DocumentConfig = {
  doc_type: 'proposal',

  labels: {
    title: 'Proposal',
    titleNew: 'Create New Proposal',
    titleEdit: 'Edit Proposal',
    numberField: 'Proposal Number',
    numberPlaceholder: 'e.g., PROP-001',
    dateField: 'Proposal Date',
    contactLabel: 'Customer',
    saveButton: 'Save Proposal'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showExpiryDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'review', label: 'In Review' },
      { value: 'pending_approval', label: 'Pending Approval' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'cancelled', label: 'Cancelled' },
      { value: 'converted', label: 'Converted' }
    ],
    userEditableStatuses: ['draft', 'review', 'pending_approval', 'approved', 'rejected', 'cancelled'],
    showReference: true,
    showNotes: true,
    showTermsConditions: true,
    showMarkup: true
  },

  conversions: [
    {
      targetType: 'quote',
      label: 'Convert to Quote',
      icon: FileCheck,
      condition: (doc) => ['approved', 'review'].includes(doc.status!)
    },
    {
      targetType: 'invoice',
      label: 'Convert to Invoice',
      icon: FileText,
      condition: (doc) => ['approved', 'review'].includes(doc.status!)
    }
  ]
};

export const QUOTE_CONFIG: DocumentConfig = {
  doc_type: 'quote',

  labels: {
    title: 'Quote',
    titleNew: 'Create New Quote',
    titleEdit: 'Edit Quote',
    numberField: 'Quote Number',
    numberPlaceholder: 'e.g., QTE-001',
    dateField: 'Quote Date',
    contactLabel: 'Customer',
    saveButton: 'Save Quote'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showExpiryDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'sent', label: 'Sent' },
      { value: 'viewed', label: 'Viewed' },
      { value: 'accepted', label: 'Accepted' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'expired', label: 'Expired' },
      { value: 'converted', label: 'Converted' }
    ],
    // Only 'accepted' and 'rejected' can be manually set by users
    // System automatically manages: draft, sent, viewed, expired, converted
    userEditableStatuses: ['accepted', 'rejected'],
    showReference: true,
    showNotes: true,
    showTermsConditions: true,
    showMarkup: true
  },

  conversions: [
    {
      targetType: 'invoice',
      label: 'Convert to Invoice',
      icon: FileText,
      condition: (doc) => doc.status === 'accepted'
    },
    {
      targetType: 'sales_order',
      label: 'Create Sales Order',
      icon: ShoppingCart,
      condition: (doc) => doc.status === 'accepted'
    }
  ]
};

export const QUOTE_REQUEST_CONFIG: DocumentConfig = {
  doc_type: 'quote_request',

  labels: {
    title: 'Quote Request',
    titleNew: 'Create Quote Request',
    titleEdit: 'Edit Quote Request',
    numberField: 'Request Number',
    numberPlaceholder: 'e.g., QR-001',
    dateField: 'Request Date',
    contactLabel: 'Customer',
    saveButton: 'Submit Request'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showExpiryDate: false,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'submitted', label: 'Submitted' },
      { value: 'under_review', label: 'Under Review' },
      { value: 'pricing_added', label: 'Pricing Added' },
      { value: 'converted', label: 'Converted to Quote' },
      { value: 'cancelled', label: 'Cancelled' }
    ],
    userEditableStatuses: ['draft'],
    showReference: true,
    showNotes: true,
    showTermsConditions: false
  },

  conversions: [
    {
      targetType: 'quote',
      label: 'Convert to Quote',
      icon: FileText,
      condition: (doc) => ['submitted', 'under_review', 'pricing_added'].includes(doc.status!)
    }
  ]
};

export const BILLING_SUBMISSION_CONFIG: DocumentConfig = {
  doc_type: 'billing_submission',

  labels: {
    title: 'Billing Submission',
    titleNew: 'Create Billing Submission',
    titleEdit: 'Edit Billing Submission',
    numberField: 'Submission Number',
    numberPlaceholder: 'e.g., BLS-001',
    dateField: 'Submitted Date',
    contactLabel: 'Customer',
    saveButton: 'Save Billing Submission'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'submitted_to_finance', label: 'Submitted to Finance' },
      { value: 'approved_for_conversion', label: 'Approved for Conversion' },
      { value: 'returned_to_operations', label: 'Returned to Operations' },
      { value: 'converted', label: 'Converted' },
      { value: 'cancelled', label: 'Cancelled' }
    ],
    userEditableStatuses: ['draft', 'submitted_to_finance'],
    showReference: true,
    showNotes: true,
    showInternalNotes: true,
    showTermsConditions: false,
    showMarkup: true
  }
};

export const CREDIT_NOTE_CONFIG: DocumentConfig = {
  doc_type: 'credit_note',

  labels: {
    title: 'Credit Note',
    titleNew: 'Create New Credit Note',
    titleEdit: 'Edit Credit Note',
    numberField: 'Credit Note Number',
    numberPlaceholder: 'e.g., CN-001',
    dateField: 'Credit Note Date',
    contactLabel: 'Customer',
    saveButton: 'Save Credit Note'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'issued', label: 'Issued' },
      { value: 'applied', label: 'Applied' },
      { value: 'refunded', label: 'Refunded' }
    ],
    userEditableStatuses: ['draft', 'issued'],
    showOriginalInvoice: true,
    showReference: true,
    showNotes: true
  },

  finalActionMenu: arDocumentFinalActionMenu({
    batchable: true,
    sendLabel: 'Issue Credit Note',
    resendLabel: 'Resend Credit Note',
  }),
};

export const PROFORMA_CONFIG: DocumentConfig = {
  doc_type: 'proforma',

  labels: {
    title: 'Proforma Invoice',
    titleNew: 'Create New Proforma Invoice',
    titleEdit: 'Edit Proforma Invoice',
    numberField: 'Proforma Number',
    numberPlaceholder: 'e.g., PRO-001',
    dateField: 'Proforma Date',
    contactLabel: 'Customer',
    saveButton: 'Save Proforma'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showExpiryDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'sent', label: 'Sent' },
      { value: 'viewed', label: 'Viewed' },
      { value: 'paid', label: 'Paid' },
      { value: 'expired', label: 'Expired' }
    ],
    userEditableStatuses: ['draft', 'sent'],
    showPaymentTerms: true,
    showReference: true,
    showNotes: true,
    showTermsConditions: true,
    showMarkup: true
  },

  conversions: [
    {
      targetType: 'invoice',
      label: 'Convert to Invoice',
      icon: FileText,
      condition: (doc) => doc.status === 'paid'
    }
  ]
};

export const DELIVERY_NOTE_CONFIG: DocumentConfig = {
  doc_type: 'delivery_note',

  labels: {
    title: 'Delivery Note',
    titleNew: 'Create New Delivery Note',
    titleEdit: 'Edit Delivery Note',
    numberField: 'Delivery Note Number',
    numberPlaceholder: 'e.g., DN-001',
    dateField: 'Delivery Date',
    contactLabel: 'Customer',
    saveButton: 'Save Delivery Note'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showDeliveryDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'in_transit', label: 'In Transit' },
      { value: 'delivered', label: 'Delivered' },
      { value: 'returned', label: 'Returned' }
    ],
    userEditableStatuses: ['draft', 'in_transit'],
    showOriginalInvoice: true,
    showPackingSlipNumber: true,
    showTrackingNumber: true,
    showCarrier: true,
    showShippingMethod: true,
    showDeliveryAddress: true,
    showSignedBy: true,
    showDeliveryInstructions: true,
    showNotes: true
  }
};

export const WAYBILL_CONFIG: DocumentConfig = {
  doc_type: 'waybill',

  labels: {
    title: 'Waybill',
    titleNew: 'Create New Waybill',
    titleEdit: 'Edit Waybill',
    numberField: 'Waybill Number',
    numberPlaceholder: 'e.g., WB-001',
    dateField: 'Waybill Date',
    contactLabel: 'Customer',
    saveButton: 'Save Waybill'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showDeliveryDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'in_transit', label: 'In Transit' },
      { value: 'delivered', label: 'Delivered' },
      { value: 'returned', label: 'Returned' }
    ],
    userEditableStatuses: ['draft', 'in_transit'],
    showOriginalInvoice: true,
    showPackingSlipNumber: true,
    showTrackingNumber: true,
    showCarrier: true,
    showShippingMethod: true,
    showDeliveryAddress: true,
    showSignedBy: true,
    showDeliveryInstructions: true,
    showNotes: true
  }
};

export const SALES_ORDER_CONFIG: DocumentConfig = {
  doc_type: 'sales_order',

  labels: {
    title: 'Sales Order',
    titleNew: 'Create New Sales Order',
    titleEdit: 'Edit Sales Order',
    numberField: 'Sales Order Number',
    numberPlaceholder: 'e.g., SO-001',
    dateField: 'Order Date',
    contactLabel: 'Customer',
    saveButton: 'Save Sales Order'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showExpectedShipDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'confirmed', label: 'Confirmed' },
      { value: 'in_production', label: 'In Production' },
      { value: 'ready_to_ship', label: 'Ready to Ship' },
      { value: 'shipped', label: 'Shipped' },
      { value: 'delivered', label: 'Delivered' },
      { value: 'cancelled', label: 'Cancelled' }
    ],
    userEditableStatuses: ['draft', 'confirmed', 'cancelled'],
    showFulfillmentStatus: true,
    fulfillmentStatusOptions: [
      { value: 'not_started', label: 'Not Started' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'partially_fulfilled', label: 'Partially Fulfilled' },
      { value: 'fulfilled', label: 'Fulfilled' }
    ],
    showCustomerPO: true,
    showShippingMethod: true,
    showShippingAddress: true,
    showNotes: true,
    showInternalNotes: true,
    showMarkup: true
  },

  conversions: [
    {
      targetType: 'work_order',
      label: 'Create Work Order',
      icon: ClipboardCheck,
      condition: (doc) => ['confirmed', 'in_production', 'ready_to_ship'].includes(doc.status!)
    },
    {
      targetType: 'invoice',
      label: 'Create Invoice',
      icon: FileText,
      condition: (doc) => doc.status === 'delivered'
    },
    {
      targetType: 'delivery_note',
      label: 'Create Delivery Note',
      icon: Truck,
      condition: (doc) => ['confirmed', 'in_production', 'ready_to_ship'].includes(doc.status!)
    }
  ]
};

// ============================================================================
// PURCHASE DOCUMENTS
// ============================================================================

export const PURCHASE_ORDER_CONFIG: DocumentConfig = {
  doc_type: 'purchase_order',

  labels: {
    title: 'Purchase Order',
    titleNew: 'Create New Purchase Order',
    titleEdit: 'Edit Purchase Order',
    numberField: 'Purchase Order Number',
    numberPlaceholder: 'e.g., PO-001',
    dateField: 'Order Date',
    contactLabel: 'Supplier',
    saveButton: 'Save Purchase Order'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showExpectedDeliveryDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'pending_approval', label: 'Pending Approval' },
      { value: 'approved', label: 'Approved' },
      { value: 'sent', label: 'Sent to Supplier' },
      { value: 'acknowledged', label: 'Acknowledged' },
      { value: 'partially_received', label: 'Partially Received' },
      { value: 'completed', label: 'Completed' },
      { value: 'cancelled', label: 'Cancelled' }
    ],
    userEditableStatuses: ['draft', 'cancelled'],
    showFulfillmentStatus: true,
    fulfillmentStatusOptions: [
      { value: 'not_started', label: 'Not Started' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'partially_fulfilled', label: 'Partially Received' },
      { value: 'fulfilled', label: 'Fully Received' }
    ],
    showSupplierReference: true,
    showShippingMethod: true,
    showDeliveryAddress: true,
    showNotes: true,
    showInternalNotes: true
  },

  conversions: [
    {
      targetType: 'grn',
      label: 'Create Goods Receipt',
      icon: Inbox,
      condition: (doc) => ['sent', 'acknowledged'].includes(doc.status!)
    }
  ]
};

export const GRN_CONFIG: DocumentConfig = {
  doc_type: 'grn',

  labels: {
    title: 'Goods Received Note',
    titleNew: 'Create New Goods Receipt',
    titleEdit: 'Edit Goods Receipt',
    numberField: 'GRN Number',
    numberPlaceholder: 'e.g., GRN-001',
    dateField: 'Receipt Date',
    contactLabel: 'Supplier',
    saveButton: 'Save Goods Receipt'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showReceivedDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'received', label: 'Received' },
      { value: 'inspected', label: 'Inspected' },
      { value: 'accepted', label: 'Accepted' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'partially_accepted', label: 'Partially Accepted' }
    ],
    userEditableStatuses: ['draft', 'received'],
    showQualityCheckStatus: true,
    qualityCheckStatusOptions: [
      { value: 'not_inspected', label: 'Not Inspected' },
      { value: 'passed', label: 'Passed' },
      { value: 'failed', label: 'Failed' },
      { value: 'conditional', label: 'Conditional Pass' }
    ],
    showOriginalInvoice: true,
    showSupplierReference: true,
    showWarehouseLocation: true,
    showInspector: true,
    showNotes: true,
    showInternalNotes: true
  },

  conversions: [
    {
      targetType: 'expense',
      label: 'Create Expense',
      icon: Receipt,
      condition: (doc) => doc.status === 'accepted'
    }
  ]
};

export const SUPPLIER_CREDIT_CONFIG: DocumentConfig = {
  doc_type: 'supplier_credit',

  labels: {
    title: 'Supplier Credit Note',
    titleNew: 'Create New Supplier Credit',
    titleEdit: 'Edit Supplier Credit',
    numberField: 'Credit Number',
    numberPlaceholder: 'e.g., SCN-001',
    dateField: 'Credit Date',
    contactLabel: 'Supplier',
    saveButton: 'Save Supplier Credit'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'issued', label: 'Issued' },
      { value: 'applied', label: 'Applied to Bill' },
      { value: 'refunded', label: 'Refunded' }
    ],
    userEditableStatuses: ['draft', 'issued'],
    showOriginalInvoice: true,
    showSupplierReference: true,
    showReference: true,
    showNotes: true
  }
};

export const SUPPLIER_QUOTE_CONFIG: DocumentConfig = {
  doc_type: 'supplier_quote',

  labels: {
    title: 'Supplier Quote',
    titleNew: 'Create New Supplier Quote',
    titleEdit: 'Edit Supplier Quote',
    numberField: 'Quote Number',
    numberPlaceholder: 'e.g., SQ-001',
    dateField: 'Quote Date',
    contactLabel: 'Supplier',
    saveButton: 'Save Supplier Quote'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showExpiryDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'received', label: 'Received' },
      { value: 'under_review', label: 'Under Review' },
      { value: 'accepted', label: 'Accepted' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'expired', label: 'Expired' }
    ],
    userEditableStatuses: ['draft', 'accepted', 'rejected'],
    showSupplierReference: true,
    showReference: true,
    showNotes: true,
    showInternalNotes: true
  },

  conversions: [
    {
      targetType: 'purchase_order',
      label: 'Create Purchase Order',
      icon: FileCheck,
      condition: (doc) => doc.status === 'accepted'
    }
  ]
};

export const PURCHASE_REQUISITION_CONFIG: DocumentConfig = {
  doc_type: 'purchase_requisition',

  labels: {
    title: 'Purchase Requisition',
    titleNew: 'Create New Purchase Requisition',
    titleEdit: 'Edit Purchase Requisition',
    numberField: 'Requisition Number',
    numberPlaceholder: 'e.g., PR-001',
    dateField: 'Requisition Date',
    contactLabel: 'Requested By',
    saveButton: 'Save Requisition'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showExpectedDeliveryDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'submitted', label: 'Submitted' },
      { value: 'under_review', label: 'Under Review' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'po_created', label: 'PO Created' }
    ],
    userEditableStatuses: ['draft', 'submitted'],
    showApprovalStatus: true,
    approvalStatusOptions: [
      { value: 'pending', label: 'Pending' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' }
    ],
    showReference: true,
    showNotes: true,
    showInternalNotes: true
  },

  conversions: [
    {
      targetType: 'purchase_order',
      label: 'Create Purchase Order',
      icon: FileCheck,
      condition: (doc) => doc.status === 'approved'
    }
  ]
};

// ============================================================================
// OPERATIONS DOCUMENTS
// ============================================================================

export const WORK_ORDER_CONFIG: DocumentConfig = {
  doc_type: 'work_order',

  labels: {
    title: 'Work Order',
    titleNew: 'Create New Work Order',
    titleEdit: 'Edit Work Order',
    numberField: 'Work Order Number',
    numberPlaceholder: 'e.g., WO-001',
    dateField: 'Work Order Date',
    contactLabel: 'Customer',
    saveButton: 'Save Work Order'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showDueDate: true,
    showExpectedDeliveryDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'pending', label: 'Pending Approval' },
      { value: 'approved', label: 'Approved' },
      { value: 'scheduled', label: 'Scheduled' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'on_hold', label: 'On Hold' },
      { value: 'completed', label: 'Completed' },
      { value: 'cancelled', label: 'Cancelled' }
    ],
    userEditableStatuses: ['draft', 'pending', 'approved', 'scheduled'],
    showReference: true,
    showCustomerPO: true,
    showWarehouseLocation: true,
    showNotes: true,
    showInternalNotes: true,
    showDeliveryInstructions: true,
    showMarkup: true
  },

  conversions: [
    {
      targetType: 'invoice',
      label: 'Create Invoice',
      icon: FileText,
      condition: (doc) => String(doc.status) === 'completed',
    },
    {
      targetType: 'delivery_note',
      label: 'Create Delivery Note',
      icon: Truck,
      condition: (doc) => ['completed', 'in_progress'].includes(String(doc.status))
    }
  ]
};

export const SUPPLIER_INVOICE_CONFIG: DocumentConfig = {
  doc_type: 'supplier_invoice',

  labels: {
    title: 'Supplier Invoice',
    titleNew: 'Create New Supplier Invoice',
    titleEdit: 'Edit Supplier Invoice',
    numberField: 'Invoice Number',
    numberPlaceholder: 'e.g., SUP-001',
    dateField: 'Invoice Date',
    contactLabel: 'Supplier',
    saveButton: 'Save Supplier Invoice'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showTransactionDate: true,
    showDueDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'received', label: 'Received' },
      { value: 'under_review', label: 'Under Review' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'paid', label: 'Paid' }
    ],
    userEditableStatuses: ['draft', 'received', 'under_review', 'approved', 'rejected'],
    showSupplierReference: true,
    showReference: true,
    showNotes: true,
    showInternalNotes: true
  },

  conversions: [],

  finalActionMenu: {
    gate: FINAL_ACTION_GATES.supplier_invoice,
    items: [
      { type: 'preview', label: 'Print Preview', icon: Printer, tone: 'primary' },
      { type: 'send', label: 'Complete & Send', tone: 'primary' },
      { type: 'complete', label: 'Complete Supplier Invoice', tone: 'success' },
    ],
  },
};

export const EXPENSE_CONFIG: DocumentConfig = {
  doc_type: 'expense',

  labels: {
    title: 'Expense',
    titleNew: 'Create New Expense',
    titleEdit: 'Edit Expense',
    numberField: 'Expense Number',
    numberPlaceholder: 'e.g., EXP-001',
    dateField: 'Expense Date',
    contactLabel: 'Supplier',
    saveButton: 'Save Expense'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showTransactionDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'submitted', label: 'Submitted' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'reimbursed', label: 'Reimbursed' }
    ],
    userEditableStatuses: ['draft', 'submitted'],
    showReference: true,
    showNotes: true,
    showMarkup: true
  },

  conversions: [
    {
      targetType: 'expense',
      label: 'Create Expense',
      icon: Receipt,
      condition: (doc) => String(doc.status) === 'approved'
    }
  ],

  finalActionMenu: {
    gate: FINAL_ACTION_GATES.expense,
    items: [
      { type: 'preview', label: 'Print Preview', icon: Printer, tone: 'primary' },
      { type: 'send', label: 'Complete & Send', tone: 'primary' },
      { type: 'complete', label: 'Complete Expense', tone: 'success' },
      {
        type: 'recurring',
        label: 'Make Recurring',
        icon: RefreshCw,
        tone: 'primary',
        condition: FINAL_ACTION_ITEM_CONDITIONS.has_id_and_contact_no_parent,
      },
    ],
  },
};

export const RMA_CONFIG: DocumentConfig = {
  doc_type: 'rma',

  labels: {
    title: 'Return Authorization',
    titleNew: 'Create Return Authorization',
    titleEdit: 'Edit Return Authorization',
    numberField: 'RMA Number',
    numberPlaceholder: 'e.g., RMA-001',
    dateField: 'RMA Date',
    contactLabel: 'Customer',
    saveButton: 'Save RMA'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showExpectedDeliveryDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'pending', label: 'Pending Approval' },
      { value: 'approved', label: 'Approved' },
      { value: 'awaiting_receipt', label: 'Awaiting Receipt' },
      { value: 'received', label: 'Received' },
      { value: 'inspecting', label: 'Inspecting' },
      { value: 'processed', label: 'Processed' },
      { value: 'refunded', label: 'Refunded' },
      { value: 'replaced', label: 'Replaced' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'closed', label: 'Closed' }
    ],
    userEditableStatuses: ['draft', 'pending'],
    showOriginalInvoice: true,
    showReference: true,
    showNotes: true,
    showInternalNotes: true
  },

  conversions: [
    {
      targetType: 'credit_note',
      label: 'Create Credit Note',
      icon: FileMinus,
      condition: (doc) => ['received', 'inspecting', 'processed'].includes(doc.status!)
    }
  ]
};

export const DEBIT_NOTE_CONFIG: DocumentConfig = {
  doc_type: 'debit_note',

  labels: {
    title: 'Debit Note',
    titleNew: 'Create New Debit Note',
    titleEdit: 'Edit Debit Note',
    numberField: 'Debit Note Number',
    numberPlaceholder: 'e.g., DN-001',
    dateField: 'Debit Note Date',
    contactLabel: 'Customer',
    saveButton: 'Save Debit Note'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showDueDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'pending', label: 'Pending Approval' },
      { value: 'sent', label: 'Sent' },
      { value: 'acknowledged', label: 'Acknowledged' },
      { value: 'applied', label: 'Applied' },
      { value: 'void', label: 'Void' }
    ],
    userEditableStatuses: ['draft', 'pending'],
    showOriginalInvoice: true,
    showReference: true,
    showPaymentTerms: true,
    showNotes: true,
    showTermsConditions: true
  },

  conversions: [],

  finalActionMenu: arDocumentFinalActionMenu({
    batchable: false,
    sendLabel: 'Issue Debit Note',
    resendLabel: 'Resend Debit Note',
  }),
};

export const RFQ_CONFIG: DocumentConfig = {
  doc_type: 'rfq',

  labels: {
    title: 'Request for Quote',
    titleNew: 'Create Request for Quote',
    titleEdit: 'Edit Request for Quote',
    numberField: 'RFQ Number',
    numberPlaceholder: 'e.g., RFQ-001',
    dateField: 'RFQ Date',
    contactLabel: 'Supplier',
    saveButton: 'Save RFQ'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showExpiryDate: true,
    showExpectedDeliveryDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'sent', label: 'Sent' },
      { value: 'awaiting_response', label: 'Awaiting Response' },
      { value: 'quote_received', label: 'Quote Received' },
      { value: 'under_review', label: 'Under Review' },
      { value: 'closed', label: 'Closed' },
      { value: 'cancelled', label: 'Cancelled' }
    ],
    userEditableStatuses: ['draft'],
    showReference: true,
    showNotes: true,
    showTermsConditions: true
  },

  conversions: [
    {
      targetType: 'supplier_quote',
      label: 'Record Supplier Quote',
      icon: FileCheck,
      condition: (doc) => ['sent', 'awaiting_response'].includes(doc.status!)
    },
    {
      targetType: 'purchase_order',
      label: 'Create Purchase Order',
      icon: ShoppingCart,
      condition: (doc) => String(doc.status) === 'quote_received'
    }
  ]
};

export const PURCHASE_RETURN_CONFIG: DocumentConfig = {
  doc_type: 'purchase_return',

  labels: {
    title: 'Purchase Return',
    titleNew: 'Create Purchase Return',
    titleEdit: 'Edit Purchase Return',
    numberField: 'Return Number',
    numberPlaceholder: 'e.g., PRET-001',
    dateField: 'Return Date',
    contactLabel: 'Supplier',
    saveButton: 'Save Purchase Return'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'pending', label: 'Pending Approval' },
      { value: 'approved', label: 'Approved' },
      { value: 'shipped', label: 'Shipped' },
      { value: 'received_by_vendor', label: 'Received by Vendor' },
      { value: 'credit_received', label: 'Credit Received' },
      { value: 'closed', label: 'Closed' },
      { value: 'cancelled', label: 'Cancelled' }
    ],
    userEditableStatuses: ['draft', 'pending'],
    showSupplierReference: true,
    showTrackingNumber: true,
    showCarrier: true,
    showWarehouseLocation: true,
    showReference: true,
    showNotes: true,
    showInternalNotes: true
  },

  conversions: [
    {
      targetType: 'supplier_credit',
      label: 'Record Supplier Credit',
      icon: RotateCcw,
      condition: (doc) => ['received_by_vendor', 'credit_received'].includes(doc.status!)
    }
  ]
};

// ============================================================================
// WAREHOUSE DOCUMENTS
// ============================================================================

export const PICKING_SLIP_CONFIG: DocumentConfig = {
  doc_type: 'picking_slip',

  labels: {
    title: 'Picking Slip',
    titleNew: 'Create Picking Slip',
    titleEdit: 'Edit Picking Slip',
    numberField: 'Picking Slip Number',
    numberPlaceholder: 'e.g., PICK-001',
    dateField: 'Pick Date',
    contactLabel: 'Customer',
    saveButton: 'Save Picking Slip'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'pending', label: 'Pending' },
      { value: 'assigned', label: 'Assigned' },
      { value: 'picking', label: 'Picking' },
      { value: 'picked', label: 'Picked' },
      { value: 'partial', label: 'Partially Picked' },
      { value: 'completed', label: 'Completed' },
      { value: 'cancelled', label: 'Cancelled' }
    ],
    userEditableStatuses: ['draft', 'pending', 'assigned'],
    showWarehouseLocation: true,
    showReference: true,
    showNotes: true,
    showInternalNotes: true
  },

  conversions: [
    {
      targetType: 'packing_slip',
      label: 'Create Packing Slip',
      icon: Package,
      condition: (doc) => ['picked', 'completed'].includes(doc.status!)
    }
  ]
};

export const PACKING_SLIP_CONFIG: DocumentConfig = {
  doc_type: 'packing_slip',

  labels: {
    title: 'Packing Slip',
    titleNew: 'Create Packing Slip',
    titleEdit: 'Edit Packing Slip',
    numberField: 'Packing Slip Number',
    numberPlaceholder: 'e.g., PACK-001',
    dateField: 'Pack Date',
    contactLabel: 'Customer',
    saveButton: 'Save Packing Slip'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'packing', label: 'Packing' },
      { value: 'packed', label: 'Packed' },
      { value: 'ready_to_ship', label: 'Ready to Ship' },
      { value: 'shipped', label: 'Shipped' },
      { value: 'cancelled', label: 'Cancelled' }
    ],
    userEditableStatuses: ['draft', 'packing'],
    showPackingSlipNumber: true,
    showTrackingNumber: true,
    showCarrier: true,
    showShippingMethod: true,
    showWarehouseLocation: true,
    showShippingAddress: true,
    showReference: true,
    showNotes: true
  },

  conversions: [
    {
      targetType: 'delivery_note',
      label: 'Create Delivery Note',
      icon: Truck,
      condition: (doc) => ['packed', 'ready_to_ship'].includes(doc.status!)
    }
  ]
};

export const STOCK_ADJUSTMENT_CONFIG: DocumentConfig = {
  doc_type: 'stock_adjustment',

  labels: {
    title: 'Stock Adjustment',
    titleNew: 'Create Stock Adjustment',
    titleEdit: 'Edit Stock Adjustment',
    numberField: 'Adjustment Number',
    numberPlaceholder: 'e.g., ADJ-001',
    dateField: 'Adjustment Date',
    contactLabel: 'Adjusted By',
    saveButton: 'Save Adjustment'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'pending', label: 'Pending Approval' },
      { value: 'approved', label: 'Approved' },
      { value: 'applied', label: 'Applied' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'cancelled', label: 'Cancelled' }
    ],
    userEditableStatuses: ['draft', 'pending'],
    showWarehouseLocation: true,
    showReference: true,
    showNotes: true,
    showInternalNotes: true
  },

  conversions: []
};

export const TRANSFER_ORDER_CONFIG: DocumentConfig = {
  doc_type: 'transfer_order',

  labels: {
    title: 'Transfer Order',
    titleNew: 'Create Transfer Order',
    titleEdit: 'Edit Transfer Order',
    numberField: 'Transfer Number',
    numberPlaceholder: 'e.g., TO-001',
    dateField: 'Transfer Date',
    contactLabel: 'Requested By',
    saveButton: 'Save Transfer Order'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showExpectedDeliveryDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'pending', label: 'Pending Approval' },
      { value: 'approved', label: 'Approved' },
      { value: 'picking', label: 'Picking' },
      { value: 'in_transit', label: 'In Transit' },
      { value: 'received', label: 'Received' },
      { value: 'partial', label: 'Partially Received' },
      { value: 'completed', label: 'Completed' },
      { value: 'cancelled', label: 'Cancelled' }
    ],
    userEditableStatuses: ['draft', 'pending'],
    showWarehouseLocation: true,
    showReference: true,
    showTrackingNumber: true,
    showCarrier: true,
    showNotes: true,
    showInternalNotes: true
  },

  conversions: []
};

export const DIN_CONFIG: DocumentConfig = {
  doc_type: 'din',

  labels: {
    title: 'Document Intake',
    titleNew: 'Review Document',
    titleEdit: 'Review Document',
    numberField: 'Intake Number',
    numberPlaceholder: 'e.g., DIN-001',
    dateField: 'Intake Date',
    contactLabel: 'Supplier',
    saveButton: 'Save Review'
  },

  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: true,
    showMainDate: true,
    showTransactionDate: true,
    showStatus: true,
    statusOptions: [
      { value: 'new', label: 'New' },
      { value: 'ready', label: 'Ready' },
      { value: 'to_review', label: 'To Review' },
      { value: 'completed', label: 'Completed' },
      { value: 'failed', label: 'Failed' },
    ],
    userEditableStatuses: ['new', 'ready', 'to_review'],
    showReference: true,
    showNotes: true,
    showMarkup: true
  },

  conversions: [
    {
      targetType: 'expense',
      label: 'Complete (Create Expense)',
      icon: Receipt,
      condition: () => true
    }
  ],

  finalActionMenu: {
    gate: FINAL_ACTION_GATES.din,
    items: [
      { type: 'preview', label: 'Print Preview', icon: Printer, tone: 'primary' },
      // "Create Expense" — simplified and standard modes.
      // In simplified mode: automation auto-promotes without human review.
      // In standard mode: the user reviewing the DIN in ExpenseInbox has
      //   permission to create expenses. Clicking this IS the approval action —
      //   the expense is created as 'approved' and goes straight to
      //   FinanceDocumentView. No second approval step is needed.
      {
        type: 'complete',
        label: 'Create Expense',
        icon: Receipt,
        tone: 'success',
        condition: (doc) => {
          const wfMode = (doc as Partial<Document> & { _workflowMode?: string })._workflowMode;
          // Show in simplified and standard modes (and when mode is unknown,
          // which defaults to 'standard' on the server).
          return !wfMode || wfMode === 'standard' || wfMode === 'simplified';
        },
      },
      // "Submit for Approval" — strict mode only.
      // Materializes the DIN into an expense with 'pending_approval' status.
      // The expense goes to DocumentApprovalBoard for multi-step approval chain
      // as configured in ApprovalChainConfig.
      {
        type: 'submit_for_approval',
        label: 'Submit for Approval',
        icon: FileCheck,
        tone: 'primary',
        condition: (doc) => {
          const wfMode = (doc as Partial<Document> & { _workflowMode?: string })._workflowMode;
          return wfMode === 'strict';
        },
      },
    ],
  },
};

// ============================================================================
// PROPERTY FINANCE DOCUMENTS
//
// Client-side mirror of the PROPERTY FINANCE DOCUMENTS block in
// config/document-modal/document-configs.yaml. The YAML registry only
// initializes server-side, so these entries are what DocumentModal actually
// resolves in the browser — keep them in sync with the YAML.
// ============================================================================

const PROPERTY_FINANCE_CONTACTS = ['customer'];

export const LEVY_CYCLE_CONFIG: DocumentConfig = {
  doc_type: 'levy_cycle',
  labels: {
    title: 'Levy Cycle',
    titleNew: 'Create Levy Cycle',
    titleEdit: 'Edit Levy Cycle',
    numberField: 'Run Number',
    numberPlaceholder: 'e.g., LEV-001',
    dateField: 'Run Date',
    contactLabel: 'Scheme',
    saveButton: 'Save Levy Cycle'
  },
  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    showNotes: true,
    contactTypes: PROPERTY_FINANCE_CONTACTS,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'scheduled', label: 'Scheduled' },
      { value: 'billing', label: 'Billing' },
      { value: 'billed', label: 'Billed' },
      { value: 'reconciling', label: 'Reconciling' },
      { value: 'closed', label: 'Closed' },
      { value: 'cancelled', label: 'Cancelled' }
    ],
    userEditableStatuses: ['draft', 'scheduled']
  }
};

export const METER_READING_CONFIG: DocumentConfig = {
  doc_type: 'meter_reading',
  labels: {
    title: 'Meter Reading',
    titleNew: 'Capture Meter Reading',
    titleEdit: 'Edit Meter Reading',
    numberField: 'Reading Number',
    numberPlaceholder: 'e.g., MRD-001',
    dateField: 'Reading Date',
    contactLabel: 'Unit',
    saveButton: 'Save Reading'
  },
  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    showNotes: true,
    contactTypes: PROPERTY_FINANCE_CONTACTS,
    statusOptions: [
      { value: 'pending', label: 'Pending' },
      { value: 'captured', label: 'Captured' },
      { value: 'verified', label: 'Verified' },
      { value: 'billed', label: 'Billed' },
      { value: 'disputed', label: 'Disputed' }
    ],
    userEditableStatuses: ['pending', 'captured']
  }
};

export const UTILITY_RECOVERY_CONFIG: DocumentConfig = {
  doc_type: 'utility_recovery',
  labels: {
    title: 'Utility Recovery',
    titleNew: 'Create Utility Recovery',
    titleEdit: 'Edit Utility Recovery',
    numberField: 'Recovery Number',
    numberPlaceholder: 'e.g., UTR-001',
    dateField: 'Period Start',
    contactLabel: 'Scheme',
    saveButton: 'Save Recovery'
  },
  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    showNotes: true,
    contactTypes: PROPERTY_FINANCE_CONTACTS,
    statusOptions: [
      { value: 'period_open', label: 'Period Open' },
      { value: 'readings', label: 'Readings' },
      { value: 'calculated', label: 'Calculated' },
      { value: 'billed', label: 'Billed' },
      { value: 'reconciled', label: 'Reconciled' },
      { value: 'closed', label: 'Closed' }
    ],
    userEditableStatuses: ['period_open', 'readings', 'calculated']
  }
};

export const SPECIAL_LEVY_CONFIG: DocumentConfig = {
  doc_type: 'special_levy',
  labels: {
    title: 'Special Levy',
    titleNew: 'Create Special Levy',
    titleEdit: 'Edit Special Levy',
    numberField: 'Levy Number',
    numberPlaceholder: 'e.g., SPL-001',
    dateField: 'Resolution Date',
    contactLabel: 'Scheme',
    saveButton: 'Save Special Levy'
  },
  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    showNotes: true,
    contactTypes: PROPERTY_FINANCE_CONTACTS,
    statusOptions: [
      { value: 'proposed', label: 'Proposed' },
      { value: 'approved', label: 'Approved' },
      { value: 'levied', label: 'Levied' },
      { value: 'collecting', label: 'Collecting' },
      { value: 'closed', label: 'Closed' },
      { value: 'rejected', label: 'Rejected' }
    ],
    userEditableStatuses: ['proposed']
  }
};

export const SCHEME_BUDGET_CONFIG: DocumentConfig = {
  doc_type: 'scheme_budget',
  labels: {
    title: 'Scheme Budget',
    titleNew: 'Create Scheme Budget',
    titleEdit: 'Edit Scheme Budget',
    numberField: 'Budget Number',
    numberPlaceholder: 'e.g., BUD-001',
    dateField: 'Financial Year Start',
    contactLabel: 'Scheme',
    saveButton: 'Save Budget'
  },
  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    showNotes: true,
    contactTypes: PROPERTY_FINANCE_CONTACTS,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'circulated', label: 'Circulated' },
      { value: 'approved', label: 'Approved' },
      { value: 'adopted', label: 'Adopted' },
      { value: 'archived', label: 'Archived' }
    ],
    userEditableStatuses: ['draft', 'circulated']
  }
};

export const RESERVE_FUND_CONFIG: DocumentConfig = {
  doc_type: 'reserve_fund',
  labels: {
    title: 'Reserve Fund Plan',
    titleNew: 'Create Reserve Fund Plan',
    titleEdit: 'Edit Reserve Fund Plan',
    numberField: 'Plan Number',
    numberPlaceholder: 'e.g., RSV-001',
    dateField: 'Plan Date',
    contactLabel: 'Scheme',
    saveButton: 'Save Plan'
  },
  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    showNotes: true,
    contactTypes: PROPERTY_FINANCE_CONTACTS,
    statusOptions: [
      { value: 'planning', label: 'Planning' },
      { value: 'active', label: 'Active' },
      { value: 'funded', label: 'Funded' },
      { value: 'archived', label: 'Archived' }
    ],
    userEditableStatuses: ['planning']
  }
};

export const DEPOSIT_HOLDING_CONFIG: DocumentConfig = {
  doc_type: 'deposit_holding',
  labels: {
    title: 'Deposit Holding',
    titleNew: 'Record Deposit',
    titleEdit: 'Edit Deposit Holding',
    numberField: 'Deposit Number',
    numberPlaceholder: 'e.g., DEP-001',
    dateField: 'Receipt Date',
    contactLabel: 'Tenant',
    saveButton: 'Save Deposit'
  },
  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    showNotes: true,
    contactTypes: PROPERTY_FINANCE_CONTACTS,
    statusOptions: [
      { value: 'pending_receipt', label: 'Pending Receipt' },
      { value: 'held', label: 'Held in Trust' },
      { value: 'refund_pending', label: 'Refund Pending' },
      { value: 'refunded', label: 'Refunded' },
      { value: 'forfeited', label: 'Forfeited' },
      { value: 'disputed', label: 'Disputed' }
    ],
    userEditableStatuses: ['pending_receipt', 'held']
  }
};

export const TRUST_TRANSACTION_CONFIG: DocumentConfig = {
  doc_type: 'trust_transaction',
  labels: {
    title: 'Trust Transaction',
    titleNew: 'Record Trust Transaction',
    titleEdit: 'Edit Trust Transaction',
    numberField: 'Entry Number',
    numberPlaceholder: 'e.g., TRX-001',
    dateField: 'Transaction Date',
    contactLabel: 'Beneficiary',
    saveButton: 'Save Entry'
  },
  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    showReference: true,
    showNotes: true,
    contactTypes: PROPERTY_FINANCE_CONTACTS,
    statusOptions: [
      { value: 'pending', label: 'Pending' },
      { value: 'posted', label: 'Posted' },
      { value: 'reconciled', label: 'Reconciled' },
      { value: 'reversed', label: 'Reversed' }
    ],
    userEditableStatuses: ['pending']
  }
};

export const PAYMENT_DISPUTE_CONFIG: DocumentConfig = {
  doc_type: 'payment_dispute',
  labels: {
    title: 'Payment Dispute',
    titleNew: 'Log Payment Dispute',
    titleEdit: 'Edit Payment Dispute',
    numberField: 'Dispute Number',
    numberPlaceholder: 'e.g., DSP-001',
    dateField: 'Submitted Date',
    contactLabel: 'Customer',
    saveButton: 'Save Dispute'
  },
  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    showNotes: true,
    contactTypes: PROPERTY_FINANCE_CONTACTS,
    statusOptions: [
      { value: 'submitted', label: 'Submitted' },
      { value: 'investigating', label: 'Investigating' },
      { value: 'customer_response', label: 'Awaiting Customer' },
      { value: 'escalated', label: 'Escalated' },
      { value: 'resolved_accepted', label: 'Resolved - Accepted' },
      { value: 'resolved_rejected', label: 'Resolved - Rejected' }
    ],
    userEditableStatuses: ['submitted', 'investigating', 'customer_response']
  }
};

export const PAYMENT_PLAN_CONFIG: DocumentConfig = {
  doc_type: 'payment_plan',
  labels: {
    title: 'Payment Plan',
    titleNew: 'Create Payment Plan',
    titleEdit: 'Edit Payment Plan',
    numberField: 'Plan Number',
    numberPlaceholder: 'e.g., PPL-001',
    dateField: 'Start Date',
    contactLabel: 'Customer',
    saveButton: 'Save Plan'
  },
  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    showNotes: true,
    contactTypes: PROPERTY_FINANCE_CONTACTS,
    statusOptions: [
      { value: 'proposed', label: 'Proposed' },
      { value: 'active', label: 'Active' },
      { value: 'missed_payment', label: 'Missed Payment' },
      { value: 'completed', label: 'Completed' },
      { value: 'defaulted', label: 'Defaulted' }
    ],
    userEditableStatuses: ['proposed', 'active']
  }
};

export const COLLECTION_CASE_CONFIG: DocumentConfig = {
  doc_type: 'collection_case',
  labels: {
    title: 'Collection Case',
    titleNew: 'Open Collection Case',
    titleEdit: 'Edit Collection Case',
    numberField: 'Case Number',
    numberPlaceholder: 'e.g., CSE-001',
    dateField: 'Opened Date',
    contactLabel: 'Customer',
    saveButton: 'Save Case'
  },
  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    showNotes: true,
    contactTypes: PROPERTY_FINANCE_CONTACTS,
    statusOptions: [
      { value: 'new', label: 'New Case' },
      { value: 'first_contact', label: 'First Contact' },
      { value: 'negotiating', label: 'Negotiating' },
      { value: 'payment_arrangement', label: 'Payment Arrangement' },
      { value: 'legal_action', label: 'Legal Action' },
      { value: 'recovered', label: 'Recovered' },
      { value: 'write_off', label: 'Written Off' }
    ],
    userEditableStatuses: ['new', 'first_contact', 'negotiating', 'payment_arrangement']
  }
};

export const INVOICE_FOLLOWUP_CONFIG: DocumentConfig = {
  doc_type: 'invoice_followup',
  labels: {
    title: 'Invoice Follow-Up',
    titleNew: 'Log Invoice Follow-Up',
    titleEdit: 'Edit Invoice Follow-Up',
    numberField: 'Follow-Up Number',
    numberPlaceholder: 'e.g., IFU-001',
    dateField: 'Follow-Up Date',
    contactLabel: 'Customer',
    saveButton: 'Save Follow-Up'
  },
  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showDueDate: true,
    showStatus: true,
    showNotes: true,
    contactTypes: PROPERTY_FINANCE_CONTACTS,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'sent', label: 'Sent' },
      { value: 'outstanding', label: 'Outstanding' },
      { value: 'overdue', label: 'Overdue' },
      { value: 'dispute', label: 'In Dispute' },
      { value: 'payment_pending', label: 'Payment Pending' },
      { value: 'paid', label: 'Paid' },
      { value: 'written_off', label: 'Written Off' }
    ],
    userEditableStatuses: ['draft', 'sent', 'outstanding', 'overdue']
  }
};

export const CREDIT_REQUEST_CONFIG: DocumentConfig = {
  doc_type: 'credit_request',
  labels: {
    title: 'Credit Request',
    titleNew: 'Create Credit Request',
    titleEdit: 'Edit Credit Request',
    numberField: 'Request Number',
    numberPlaceholder: 'e.g., CRQ-001',
    dateField: 'Request Date',
    contactLabel: 'Customer',
    saveButton: 'Save Request'
  },
  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    showNotes: true,
    contactTypes: PROPERTY_FINANCE_CONTACTS,
    statusOptions: [
      { value: 'requested', label: 'Requested' },
      { value: 'pending_approval', label: 'Pending Approval' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'issued', label: 'Credit Issued' }
    ],
    userEditableStatuses: ['requested', 'pending_approval']
  },
  conversions: [
    {
      targetType: 'credit_note',
      label: 'Issue Credit Note',
      icon: FileMinus,
      condition: (doc) => doc.status === 'approved'
    }
  ]
};

export const WRITE_OFF_CONFIG: DocumentConfig = {
  doc_type: 'write_off',
  labels: {
    title: 'Write-Off',
    titleNew: 'Request Write-Off',
    titleEdit: 'Edit Write-Off',
    numberField: 'Write-Off Number',
    numberPlaceholder: 'e.g., WOF-001',
    dateField: 'Request Date',
    contactLabel: 'Customer',
    saveButton: 'Save Write-Off'
  },
  fields: {
    showDocumentNumber: true,
    documentNumberReadOnly: false,
    showMainDate: true,
    showStatus: true,
    showNotes: true,
    contactTypes: PROPERTY_FINANCE_CONTACTS,
    statusOptions: [
      { value: 'requested', label: 'Requested' },
      { value: 'pending_approval', label: 'Pending Approval' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'processed', label: 'Processed' }
    ],
    userEditableStatuses: ['requested', 'pending_approval']
  }
};

// ============================================================================
// CONFIG MAP (For Easy Lookup)
// ============================================================================

export const DOCUMENT_CONFIGS: Record<string, DocumentConfig> = {
  // Sales
  invoice: INVOICE_CONFIG,
  quote: QUOTE_CONFIG,
  proposal: PROPOSAL_CONFIG,
  quote_request: QUOTE_REQUEST_CONFIG,
  billing_submission: BILLING_SUBMISSION_CONFIG,
  credit_note: CREDIT_NOTE_CONFIG,
  debit_note: DEBIT_NOTE_CONFIG,
  proforma: PROFORMA_CONFIG,
  delivery_note: DELIVERY_NOTE_CONFIG,
  waybill: WAYBILL_CONFIG,
  sales_order: SALES_ORDER_CONFIG,
  rma: RMA_CONFIG,

  // Purchases
  purchase_order: PURCHASE_ORDER_CONFIG,
  supplier_invoice: SUPPLIER_INVOICE_CONFIG,
  expense: EXPENSE_CONFIG,
  grn: GRN_CONFIG,
  supplier_credit: SUPPLIER_CREDIT_CONFIG,
  supplier_quote: SUPPLIER_QUOTE_CONFIG,
  purchase_requisition: PURCHASE_REQUISITION_CONFIG,
  rfq: RFQ_CONFIG,
  purchase_return: PURCHASE_RETURN_CONFIG,

  // Operations
  work_order: WORK_ORDER_CONFIG,

  // Warehouse
  picking_slip: PICKING_SLIP_CONFIG,
  packing_slip: PACKING_SLIP_CONFIG,
  stock_adjustment: STOCK_ADJUSTMENT_CONFIG,
  transfer_order: TRANSFER_ORDER_CONFIG,
  // Document Intake (work item type, not a finance document type)
  din: DIN_CONFIG,

  // Property Finance
  levy_cycle: LEVY_CYCLE_CONFIG,
  meter_reading: METER_READING_CONFIG,
  utility_recovery: UTILITY_RECOVERY_CONFIG,
  special_levy: SPECIAL_LEVY_CONFIG,
  scheme_budget: SCHEME_BUDGET_CONFIG,
  reserve_fund: RESERVE_FUND_CONFIG,
  deposit_holding: DEPOSIT_HOLDING_CONFIG,
  trust_transaction: TRUST_TRANSACTION_CONFIG,
  payment_dispute: PAYMENT_DISPUTE_CONFIG,
  payment_plan: PAYMENT_PLAN_CONFIG,
  collection_case: COLLECTION_CASE_CONFIG,
  invoice_followup: INVOICE_FOLLOWUP_CONFIG,
  credit_request: CREDIT_REQUEST_CONFIG,
  write_off: WRITE_OFF_CONFIG
};

/**
 * Fallback sets used only when the SSOT registry is not yet initialized.
 * Once types.yaml is loaded, terminal/system status lookups come from configLoader.
 */
const FALLBACK_DEFAULT_EDITABLE_STATUSES = new Set([
  'draft',
  'pending',
  'pending_approval',
]);

const FALLBACK_TERMINAL_NON_EDITABLE_STATUSES = new Set([
  'accepted',
  'applied',
  'cancelled',
  'closed',
  'completed',
  'converted',
  'credit_received',
  'declined',
  'delivered',
  'expired',
  'fulfilled',
  'overdue',
  'paid',
  'partially_paid',
  'processed',
  'received_by_vendor',
  'refunded',
  'rejected',
  'replaced',
  'void',
]);

const FALLBACK_ADDITIONAL_EDITABLE_STATUSES: Record<string, string[]> = {
  quote: ['sent', 'viewed'],
};

function normalizeStatus(status?: string | null): string {
  return String(status || '').trim().toLowerCase();
}

/**
 * Derive status options (value/label pairs) from the SSOT (types.yaml via
 * configLoader). Falls back to the deprecated hardcoded `statusOptions` in
 * the DocumentConfig when the registry is not yet initialized.
 */
export function getDocumentStatusOptions(docType: string): Array<{ value: string; label: string }> {
  const registry = getWorkItemTypeRegistry();
  if (registry.isInitialized()) {
    const statuses: StatusDefinition[] = registry.getStatuses(docType);
    if (statuses.length > 0) {
      return statuses.map(s => ({ value: s.key, label: s.label }));
    }
  }
  const config = getDocumentConfig(docType);
  return config.fields.statusOptions || [];
}

/**
 * Returns the set of statuses that the user is allowed to manually set for
 * a given document type. Uses the SSOT (types.yaml) to filter out terminal
 * and system-managed statuses. Falls back to hardcoded sets when the
 * registry is not yet initialized.
 */
export function getEditableDocumentStatuses(docType: string): string[] {
  const registry = getWorkItemTypeRegistry();
  if (registry.isInitialized()) {
    const statuses: StatusDefinition[] = registry.getStatuses(docType);
    if (statuses.length > 0) {
      return statuses
        .filter(s => !s.is_terminal && !s.is_system)
        .map(s => s.key);
    }
  }

  // Fallback: hardcoded logic for when SSOT is not yet loaded
  const config = getDocumentConfig(docType);
  const editableStatuses = new Set<string>(FALLBACK_DEFAULT_EDITABLE_STATUSES);

  (config.fields.userEditableStatuses || []).forEach((status) => {
    const normalized = normalizeStatus(status);
    if (normalized && !FALLBACK_TERMINAL_NON_EDITABLE_STATUSES.has(normalized)) {
      editableStatuses.add(normalized);
    }
  });

  (FALLBACK_ADDITIONAL_EDITABLE_STATUSES[docType] || []).forEach((status) => {
    const normalized = normalizeStatus(status);
    if (normalized) {
      editableStatuses.add(normalized);
    }
  });

  return Array.from(editableStatuses);
}

export function isDocumentEditableStatus(docType: string, status?: string | null): boolean {
  const normalizedStatus = normalizeStatus(status || 'draft');

  // Try SSOT first
  const registry = getWorkItemTypeRegistry();
  if (registry.isInitialized()) {
    const statuses: StatusDefinition[] = registry.getStatuses(docType);
    if (statuses.length > 0) {
      const statusDef = statuses.find(s => s.key === normalizedStatus);
      if (statusDef) {
        return !statusDef.is_terminal && !statusDef.is_system;
      }
      // Status not found in SSOT for this type — not editable
      return false;
    }
  }

  // Fallback to hardcoded logic
  return getEditableDocumentStatuses(docType).includes(normalizedStatus);
}

/**
 * Get document config by type.
 * Tries the YAML registry (source of truth) first, falls back to hardcoded configs.
 */
export function getDocumentConfig(docType: string): DocumentConfig {
  const registry = getDocumentModalRegistry();
  if (registry.isInitialized()) {
    const yamlConfig = registry.getDocumentConfig(docType);
    if (yamlConfig) {
      return resolveDocumentConfig(docType, yamlConfig);
    }
  }
  return DOCUMENT_CONFIGS[docType] || INVOICE_CONFIG;
}

/**
 * Get promotion targets for a document type from the YAML config.
 * Returns an array of { type, label, icon, description } objects.
 * Filters by condition_statuses if the source document status is provided.
 */
export function getPromotionTargetsFromYaml(
  docType: DocumentType,
  status?: string,
): Array<{ type: DocumentType; label: string; icon: string; description?: string }> {
  const registry = getDocumentModalRegistry();
  if (!registry.isInitialized()) return [];

  const targets: PromotionTargetYaml[] = registry.getPromotionTargets(docType);
  return targets
    .filter((t) => {
      if (!t.condition_statuses) return true;
      if (!status) return false;
      return t.condition_statuses.includes(status);
    })
    .map((t) => ({
      type: t.type as DocumentType,
      label: t.label,
      icon: t.icon,
      description: t.description,
    }));
}

/**
 * Get all sales document configs
 */
export function getSalesConfigs(): DocumentConfig[] {
  return [
    INVOICE_CONFIG,
    QUOTE_CONFIG,
    CREDIT_NOTE_CONFIG,
    DEBIT_NOTE_CONFIG,
    PROFORMA_CONFIG,
    DELIVERY_NOTE_CONFIG,
    WAYBILL_CONFIG,
    SALES_ORDER_CONFIG,
    RMA_CONFIG
  ];
}

/**
 * Get all purchase document configs
 */
export function getPurchaseConfigs(): DocumentConfig[] {
  return [
    PURCHASE_ORDER_CONFIG,
    SUPPLIER_INVOICE_CONFIG,
    EXPENSE_CONFIG,
    GRN_CONFIG,
    SUPPLIER_CREDIT_CONFIG,
    SUPPLIER_QUOTE_CONFIG,
    PURCHASE_REQUISITION_CONFIG,
    RFQ_CONFIG,
    PURCHASE_RETURN_CONFIG
  ];
}

/**
 * Get all operations document configs
 */
export function getOperationsConfigs(): DocumentConfig[] {
  return [
    WORK_ORDER_CONFIG,
    EXPENSE_CONFIG
  ];
}

/**
 * Get all warehouse document configs
 */
export function getWarehouseConfigs(): DocumentConfig[] {
  return [
    PICKING_SLIP_CONFIG,
    PACKING_SLIP_CONFIG,
    STOCK_ADJUSTMENT_CONFIG,
    TRANSFER_ORDER_CONFIG
  ];
}

/**
 * Check if document type is sales-related
 */
export function isSalesDocument(docType: string): boolean {
  return ['invoice', 'quote', 'credit_note', 'debit_note', 'proforma', 'delivery_note', 'waybill', 'sales_order', 'rma'].includes(docType);
}

/**
 * Check if document type is purchase-related
 */
export function isPurchaseDocument(docType: string): boolean {
  return ['purchase_order', 'supplier_invoice', 'expense', 'grn', 'supplier_credit', 'supplier_quote', 'purchase_requisition', 'rfq', 'purchase_return'].includes(docType);
}

/**
 * Check if document type is operations-related
 */
export function isOperationsDocument(docType: string): boolean {
  return ['work_order'].includes(docType);
}

/**
 * Check if document type is warehouse-related
 */
export function isWarehouseDocument(docType: string): boolean {
  return ['picking_slip', 'packing_slip', 'stock_adjustment', 'transfer_order'].includes(docType);
}
